import { Component, OnInit } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { getUser, State } from "app/reducers";
import {
  GroupSectionBaseAbstractComponent,
  SelectButtonsData
} from "app/shared/modules/group-section/components/group-section-base.abstract.component";
import { AdditionalIncomeService } from "app/modules/statement/services/additional-income.service";
import {
  StepsControllerService,
  SubStepSaveDataInterface
} from "app/core/workflow/steps-controller/steps-controller.service";
import { VatDeclarationSubSteps } from "app/core/workflow/steps-controller/sub-steps/additional-income/vat-declaration.sub-steps";
import { VatDeclarationManager } from "app/modules/statement/models/additional-income/vat-declaration.manager";
import { AutofocusHelper } from "app/core/helpers/autofocus.helper";

import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { VatDeclaration } from "../../../models/additional-income/vat-declaration.model";
import { User } from "app/users";

@Component({
  ...GroupSectionBaseAbstractComponent.metaData,
  selector: "app-additional-income-group-vat",
  templateUrl: "./additional-income-group-vat.component.html",
  standalone: false
})
export class AdditionalIncomeGroupVatComponent extends GroupSectionBaseAbstractComponent implements OnInit {
  public groupTitle: string;
  public vatDeclarationManager = VatDeclarationManager;
  public isBusiness: boolean = false;

  constructor(
    public additionalIncomeService: AdditionalIncomeService,
    public dialog: MatDialog,
    public store: Store<State>,
    public stepsControllerService: StepsControllerService,
    public sanitizer: DomSanitizer
  ) {
    super(dialog, store, stepsControllerService);
  }

  ngOnInit() {
    this.groupTitle = this.title;
    this.init();

    this.store
      .select(getUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user: User) => {
        if (user) {
          this.isBusiness = user.isBusiness;
        }
      });

    this.stepsControllerService.reInitEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.init();
    });

    this.stepsControllerService.subStepSaveEmitter.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data: SubStepSaveDataInterface) => this.saveGroup(data)
    });

    super.ngOnInit();
  }

  public isSelected = (selectButtonName: string): boolean => {
    switch (selectButtonName) {
      case VatDeclarationSubSteps.VAT_DECLARATION:
        const vat = VatDeclarationManager.getVatDeclaration(this.additionalIncomeService.currentStatement)?.[0];
        return !!vat && !!(vat as any).declaration_type;
    }

    return false;
  };

  public selectButtonClickAction = (selectButtonName: string): void => {
    switch (selectButtonName) {
      case VatDeclarationSubSteps.VAT_DECLARATION:
        const vat = VatDeclarationManager.getVatDeclaration(this.additionalIncomeService.currentStatement)?.[0];

        const hasVatDeclaration = !!vat && !!(vat as any).declaration_type;

        if (hasVatDeclaration) {
          this.showAreYouSurePopup((dialog) => {
            VatDeclarationManager.removeVatDeclaration(this.additionalIncomeService.currentStatement);
            this.updateStatement(() => {
              dialog.close();
            });
          });
        } else {
          VatDeclarationManager.createVatDeclaration(this.additionalIncomeService.currentStatement);
        }
        break;
    }
    this.selectedButtonsChanged();
  };

  private setSelectButtonsData = (): void => {
    const selectButtonsData: SelectButtonsData[] = [
      {
        id: VatDeclarationSubSteps.VAT_DECLARATION,
        image: "assets/img/additional_income/ikona-vat.svg",
        title: "ADDITIONAL_INCOME.VAT_DECLARATION_GROUP.BTN_TITLE"
      }
    ];

    this.setSelectButtons(selectButtonsData);
  };

  private setSteps(): void {
    this.subSteps = [
      {
        id: VatDeclarationSubSteps.MAIN,
        isActive: true
      },
      {
        id: VatDeclarationSubSteps.VAT_DECLARATION,
        isActive: false
      }
    ];
  }

  private updateStatement(callback = () => {}): void {
    this.additionalIncomeService.updateAdditionalIncome(this.getActiveSubStep().id as VatDeclarationSubSteps, callback);
  }

  private saveGroup({ index, callback }: SubStepSaveDataInterface): void {
    const currentStep = this.subSteps[index];

    if (currentStep && currentStep.id !== VatDeclarationSubSteps.MAIN) {
      this.additionalIncomeService.formValidationErrors(currentStep.id as VatDeclarationSubSteps);
      if (this.additionalIncomeService.epValidator.length) {
        callback(false);
        return;
      }
    }

    callback(true);
    AutofocusHelper.autofocusMatSelectMainContent();
  }

  private init(): void {
    this.setSteps();
    this.setSelectButtonsData();

    AutofocusHelper.autofocusMatSelectMainContent();
  }

  protected readonly VatDeclarationSubSteps = VatDeclarationSubSteps;
}
